import { Box, Button, Paper, Switch, FormControlLabel } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Vocabolario } from "../types"
import {
  useUnificatVocabolari,
  useVocabolariList,
  useVocabolarioCreate,
  useVocabolarioDelete,
  useVocabolarioUpdate,
} from "../hooks/vocabolari"
import { Title } from "@mui/icons-material"
import VocaboloForm from "../components/forms/VocaboloForm"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
import { SelectTipologiaVocabolario } from "../components/forms/formsHelpers"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import UnificaVocaboliForm from "../components/forms/UnificaVocaboliForm"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  vocabolario: params.get("vocabolario") ?? "",
  vocaboli_incompleti: params.get("vocaboli_incompleti") ?? "",
})

export default function Vocaboli() {
  const pageSize = usePageSize()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols = [
      {
        field: "vocabolario_display",
        headerName: t("vocabolario.vocabolario"),
        flex: 1,
      },
      { field: "termine_it", headerName: t("vocabolario.termine_it"), flex: 1 },
      {
        field: "termine_en",
        headerName: t("vocabolario.termine_en"),
        flex: 1,
      },
    ]
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) =>
          userCanWriteAndDelete && (
            <>
              <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
              <IconButtonDelete disabled={!params.row.can_delete} onClick={() => deleteModalActions.open(params.row)} />
            </>
          ),
      })
    }
    return cols
  }, [t, userCanWriteAndDelete])

  const { data, isLoading, refetch } = useVocabolariList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    vocabolario: filters.vocabolario,
    vocaboli_incompleti: filters.vocaboli_incompleti,
  })

  const { mutateAsync: create } = useVocabolarioCreate()
  const { mutateAsync: update } = useVocabolarioUpdate()
  const { mutateAsync: remove } = useVocabolarioDelete()
  const { mutateAsync: unificaVocabolari } = useUnificatVocabolari()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [unificaModalState, unificaModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.vocabolario) count++
    if (filters.vocaboli_incompleti) count++
    return count
  }, [filters.vocabolario, filters.vocaboli_incompleti])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("vocabolario.titolo_lista_vocaboli")}
            breadcrumbs={[
              {
                label: t("vocabolario.vocabolari"),
                to: `/${i18n.language}/vocabolari`,
                iconType: Title,
              },
              {
                label: filters.vocabolario,
              },
            ]}
          />
          <Box display={"flex"} alignItems="center" gap={1}>
            {userCanWriteAndDelete && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  unificaModalActions.open()
                }}
              >
                {t("vocabolario.cta_unifica_vocaboli")}
              </Button>
            )}
            {userCanWriteAndDelete && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  createModalActions.open(filters.vocabolario)
                }}
              >
                {t("vocabolario.cta_aggiungi")}
              </Button>
            )}
          </Box>
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, vocabolario: "", vocaboli_incompleti: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Box display={"flex"} alignItems={"center"}>
                <Box sx={{ width: 200 }}>
                  <SelectTipologiaVocabolario
                    label={t("vocabolario.vocabolario")}
                    required
                    margin="dense"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={uiFilters.vocabolario}
                    onChange={(e) => setFilters({ ...filters, vocabolario: e.target.value, page: 1 })}
                  ></SelectTipologiaVocabolario>
                </Box>
                <Box marginLeft={2} width={400}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.vocaboli_incompleti === "true"}
                        onChange={(e) =>
                          setFilters({ ...filters, vocaboli_incompleti: e.target.checked ? "true" : "", page: 1 })
                        }
                      />
                    }
                    label={t("vocabolario.vocaboli_incompleti")}
                  />
                </Box>
              </Box>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Vocabolario>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("vocabolario.cta_aggiungi")}
        FormComponent={VocaboloForm}
        formComponentProps={{vocabolario: filters.vocabolario}}
        save={(values: Vocabolario) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Vocabolario>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("vocabolario.cta_modifica")}
        
        FormComponent={VocaboloForm}
        save={(values: Vocabolario) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("vocabolario.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
      />
      <DialogForm<any>
        open={unificaModalState.isOpen}
        handleClose={unificaModalActions.close}
        title={t("vocabolario.unifica")}
        FormComponent={UnificaVocaboliForm}
        item={filters.vocabolario}
        save={(values) => {
          return unificaVocabolari(values)
            .then(() => {
              unificaModalActions.close()
              showMessage(t("vocabolario.esito_unifica_ok"))
            })
            .finally(() => {
              unificaModalActions.close()
              refetch()
              showMessage(t("vocabolario.esito_unifica_ok"))
            })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("vocabolario.cta_elimina")}
        subtitle={
          <span>
            {t("vocabolario.cta_subtitle_elimina")} <b>{deleteModalState.value?.termine_it}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("vocabolario.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
